/**
 * Service Ticket — Post-install service and issue handling entity.
 * Distinct from Issue (which is operational during a job).
 */

export type ServiceTicketStatus = "Open" | "In Progress" | "Awaiting Parts" | "Scheduled" | "Resolved" | "Closed";
export type ServiceTicketPriority = "Low" | "Medium" | "High" | "Urgent";
export type ServiceTicketCategory =
  | "Warranty Claim"
  | "Callback"
  | "Customer Complaint"
  | "Maintenance"
  | "Damage Repair"
  | "Follow-Up"
  | "Other";

export interface ServiceTicketActivity {
  id: string;
  action: string;
  user: string;
  createdAt: string;
}

export interface ServiceTicket {
  id: string;
  // Linking
  jobId: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  address: string;
  // Ticket details
  category: ServiceTicketCategory;
  priority: ServiceTicketPriority;
  status: ServiceTicketStatus;
  subject: string;
  description: string;
  // Resolution
  resolution?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  // Ownership
  assignedTo: string;
  createdBy: string;
  // Timeline
  createdAt: string;
  updatedAt: string;
  // Activity
  activities: ServiceTicketActivity[];
  // Scheduling
  scheduledDate?: string;
  scheduledTimeSlot?: string;
  // Notes
  notes: string;
}

export const SERVICE_TICKET_CATEGORIES: ServiceTicketCategory[] = [
  "Warranty Claim", "Callback", "Customer Complaint", "Maintenance", "Damage Repair", "Follow-Up", "Other",
];

export const SERVICE_TICKET_PRIORITIES: ServiceTicketPriority[] = ["Low", "Medium", "High", "Urgent"];

export const SERVICE_TICKET_STATUSES: ServiceTicketStatus[] = [
  "Open", "In Progress", "Awaiting Parts", "Scheduled", "Resolved", "Closed",
];

export const TICKET_STATUS_COLORS: Record<ServiceTicketStatus, string> = {
  Open: "bg-info/10 text-info border-info/30",
  "In Progress": "bg-warning/10 text-warning border-warning/30",
  "Awaiting Parts": "bg-primary/10 text-primary border-primary/30",
  Scheduled: "bg-info/10 text-info border-info/30",
  Resolved: "bg-success/10 text-success border-success/30",
  Closed: "bg-muted text-muted-foreground border-border",
};

export const TICKET_PRIORITY_COLORS: Record<ServiceTicketPriority, string> = {
  Low: "bg-muted text-muted-foreground border-border",
  Medium: "bg-info/10 text-info border-info/30",
  High: "bg-warning/10 text-warning border-warning/30",
  Urgent: "bg-destructive/10 text-destructive border-destructive/30",
};
